/**
 * `character_reveal_gate` renderer — composes `@nextday/funnel-ui`
 * `CharacterRevealGate`. Single-character mode (the SecretMate default,
 * plan §5 step 13) renders one centered card; multi-character mode keeps
 * the 3-up grid for explore-style funnels.
 *
 * Characters are sourced in priority order:
 * 1. `data.characters` (explicit JSON list — useful for static funnels).
 * 2. `session.matched_character` (single-character SecretMate flow).
 * 3. `session.character` (legacy character continuity).
 *
 * The CTA fires `onAdvance()` so the orchestrator records exactly one
 * funnel transition. Optional `chips` / `chipsFromAnswers` echo earlier
 * answers; static `chips` takes precedence over the dynamic projection.
 *
 * Step JSON shape (plan §5 step 13 — single-character SecretMate):
 *
 *   {
 *     "type": "character_reveal_gate",
 *     "data": {
 *       "preRevealHeadline": "Almost there...",
 *       "postRevealHeadline": "Meet {character_name}",
 *       "ctaLabel": "Send your first message",
 *       "gateMs": 1200,
 *       "chipsFromAnswers": ["communication_style", "first_conversation"]
 *     }
 *   }
 */
import { CharacterRevealGate } from "@nextday/funnel-ui";
import { z } from "zod";

import { buildChipsFromAnswers } from "@survey-kit/renderers/chips";
import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

const characterShapeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  imageUrl: z.string().min(1),
  bio: z.string().optional(),
});

export const characterRevealGateSchema = z
  .object({
    preRevealHeadline: z.string().optional(),
    postRevealHeadline: z.string().optional(),
    ctaLabel: z.string().optional(),
    gateMs: z.number().int().nonnegative().optional(),
    /** Duration in ms for the progressive unblur animation. Defaults to 1500. */
    revealDurationMs: z.number().int().nonnegative().optional(),
    /** Starting blur radius in px. Defaults to 20. */
    blurStart: z.number().nonnegative().optional(),
    /** Static character list. When omitted, the renderer reads from session. */
    characters: z.array(characterShapeSchema).optional(),
    chips: z.array(z.string().min(1)).optional(),
    chipsFromAnswers: z.array(z.string().min(1)).optional(),
  })
  .strict();

export type CharacterRevealGateData = z.infer<typeof characterRevealGateSchema>;

type RevealCharacter = {
  id: string;
  name: string;
  imageUrl: string;
  bio?: string;
};

// Image-key set must stay in sync with `normalizeCharacter` below — otherwise
// a character carrying only one of these keys would pass `normalizeCharacter`
// but be rejected by the guard, silently producing an empty characters array.
const IMAGE_KEYS = ["imageUrl", "primary_image_url", "safe_image_url"] as const;

const hasStringImageKey = (value: object): boolean =>
  IMAGE_KEYS.some((key) => typeof (value as Record<string, unknown>)[key] === "string");

const isRevealCharacter = (value: unknown): value is RevealCharacter =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as { id?: unknown }).id === "string" &&
  typeof (value as { name?: unknown }).name === "string" &&
  hasStringImageKey(value);

const pickString = (raw: Record<string, unknown>, keys: ReadonlyArray<string>): string => {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return "";
};

const normalizeCharacter = (raw: Record<string, unknown>): RevealCharacter => ({
  id: pickString(raw, ["id"]),
  name: pickString(raw, ["name"]),
  imageUrl: pickString(raw, IMAGE_KEYS),
  ...(typeof raw.bio === "string" ? { bio: raw.bio } : {}),
});

const resolveCharacters = (
  data: CharacterRevealGateData,
  session: RendererProps["session"],
): RevealCharacter[] => {
  if (data.characters && data.characters.length > 0) return data.characters;
  if (isRevealCharacter(session.matched_character)) {
    return [normalizeCharacter(session.matched_character)];
  }
  if (isRevealCharacter(session.character)) {
    return [normalizeCharacter(session.character)];
  }
  return [];
};

// Chip resolution is shared with `validation_screen` via
// `@survey-kit/renderers/chips` — see that module for the contract.
// Local re-aliasing keeps call sites readable.
const resolveChips = buildChipsFromAnswers;

const interpolateHeadline = (
  template: string | undefined,
  characterName: string | undefined,
): string | undefined => {
  if (!template) return undefined;
  if (!characterName) return template;
  return template.replaceAll("{character_name}", characterName);
};

const CharacterRevealGateRenderer = ({
  data,
  step,
  answers,
  session,
  onAdvance,
}: RendererProps<CharacterRevealGateData>) => {
  const characters = resolveCharacters(data, session);
  const chips = resolveChips(data, answers);
  const firstName = characters[0]?.name;

  return (
    <CharacterRevealGate
      characters={characters}
      gateMs={data.gateMs}
      revealDurationMs={data.revealDurationMs}
      blurStart={data.blurStart}
      preRevealHeadline={interpolateHeadline(data.preRevealHeadline, firstName)}
      postRevealHeadline={interpolateHeadline(data.postRevealHeadline, firstName)}
      ctaLabel={data.ctaLabel}
      chips={chips}
      onCta={() => onAdvance()}
      testId={`renderer-character-reveal-${step.id}`}
    />
  );
};

export const characterRevealGateRenderer = createRenderer({
  type: "character_reveal_gate",
  dataSchema: characterRevealGateSchema,
  label: "Character reveal gate",
  Component: CharacterRevealGateRenderer,
});

export default characterRevealGateRenderer;
